// utils/quotaCheck.js
// 额度检查工具函数

import { checkQuota } from '../api/membership'
import { ElMessageBox, ElMessage } from 'element-plus'

// 额度类型常量
export const QUOTA_TYPE = {
  CUSTOMER: 'CUSTOMER',
  FOLLOW: 'FOLLOW',
  DEAL: 'DEAL',
  CONTRACT: 'CONTRACT',
  ATTACHMENT: 'ATTACHMENT',
  CARD_SCAN: 'CARD_SCAN'
}

// 额度名称映射
const QUOTA_NAME_MAP = {
  [QUOTA_TYPE.CUSTOMER]: '客户数量',
  [QUOTA_TYPE.FOLLOW]: '跟进记录数量',
  [QUOTA_TYPE.DEAL]: '成交台账数量',
  [QUOTA_TYPE.CONTRACT]: '合同数量',
  [QUOTA_TYPE.ATTACHMENT]: '附件数量',
  [QUOTA_TYPE.CARD_SCAN]: '名片扫描次数'
}

// 获取localStorage中的额度超限标记key
function getQuotaExceededKey(quotaType) {
  return `quota_exceeded_${quotaType}`
}

// 设置额度超限标记
export function setQuotaExceeded(quotaType) {
  const key = getQuotaExceededKey(quotaType)
  localStorage.setItem(key, 'true')
}

// 清除额度超限标记
function clearQuotaExceeded(quotaType) {
  const key = getQuotaExceededKey(quotaType)
  localStorage.removeItem(key)
}

// 检查是否已标记为额度超限
function isQuotaExceeded(quotaType) {
  const key = getQuotaExceededKey(quotaType)
  return localStorage.getItem(key) === 'true'
}

/**
 * 检查额度并处理超限情况
 * @param {string} quotaType 额度类型
 * @param {Object} options 选项
 * @param {boolean} options.allowLocalOperation 是否允许本地操作（如果额度到了，允许本次操作，但记录状态）
 * @param {string} options.operationName 操作名称（用于提示信息）
 * @returns {Promise<{allowed: boolean, quota: Object|null}>} 返回是否允许操作和额度信息
 */
export async function checkQuotaWithLocalFallback(quotaType, options = {}) {
  const { allowLocalOperation = false, operationName = QUOTA_NAME_MAP[quotaType] || '操作' } = options
  
  // 如果之前已经标记为超限，先获取当前额度信息再判断
  if (isQuotaExceeded(quotaType)) {
    try {
      const quotaRes = await checkQuota(quotaType)
      if (quotaRes && quotaRes.code === '000000' && quotaRes.data) {
        const quota = quotaRes.data
        // 如果 limit 为 null（无限制）或 exceeded 为 false（未超限），清除标记并允许操作
        if (quota.limit === null || quota.exceeded === false) {
          clearQuotaExceeded(quotaType)
          // 继续执行后续的额度检查逻辑
        } else {
          // 确实超限了，弹窗提示
          const quotaName = QUOTA_NAME_MAP[quotaType] || '额度'
          let message = `您的${quotaName}已达上限`
          if (quota.limit !== null && quota.used !== null) {
            message += `（${quota.used}/${quota.limit}）`
          }
          message += `，无法继续${operationName}。\n\n请到小程序： 我的 -> 会员中心去升级会员以提升额度~`
          await ElMessageBox.alert(
            message,
            '额度已用完',
            {
              confirmButtonText: '确认',
              type: 'warning'
            }
          )
          return { allowed: false, quota }
        }
      } else {
        // 如果获取额度失败，清除标记，继续执行后续逻辑（降级处理）
        clearQuotaExceeded(quotaType)
      }
    } catch (error) {
      // 如果获取额度失败，清除标记，继续执行后续逻辑（降级处理）
      clearQuotaExceeded(quotaType)
      console.warn(`检查${QUOTA_NAME_MAP[quotaType] || quotaType}额度失败，清除超限标记:`, error)
    }
  }

  try {
    const quotaRes = await checkQuota(quotaType)
    if (quotaRes && quotaRes.code === '000000' && quotaRes.data) {
      const quota = quotaRes.data
      
      // 检查是否超限
      if (quota.limit !== null && quota.used !== null && quota.exceeded) {
        // 额度已超限
        if (allowLocalOperation) {
          // 允许本次操作，但记录状态
          setQuotaExceeded(quotaType)
          const quotaName = QUOTA_NAME_MAP[quotaType] || '额度'
          await ElMessageBox.confirm(
            `您的${quotaName}已达上限（${quota.used}/${quota.limit}），本次${operationName}后将无法继续${operationName}。\n\n请到小程序： 我的 -> 会员中心去升级会员以提升额度~`,
            '额度已用完',
            {
              confirmButtonText: '继续操作',
              cancelButtonText: '取消',
              type: 'warning',
              distinguishCancelAndClose: false
            }
          ).catch(() => {
            // 用户点击取消，清除标记
            clearQuotaExceeded(quotaType)
            return { allowed: false, quota }
          })
          // 用户确认继续操作
          return { allowed: true, quota, exceeded: true }
        } else {
          // 不允许操作，直接弹窗提示
          const quotaName = QUOTA_NAME_MAP[quotaType] || '额度'
          await ElMessageBox.confirm(
            `您的${quotaName}已达上限（${quota.used}/${quota.limit}），无法继续${operationName}。\n\n请到小程序： 我的 -> 会员中心去升级会员以提升额度~`,
            '额度已用完',
            {
              confirmButtonText: '确认',
              cancelButtonText: '取消',
              type: 'warning',
              distinguishCancelAndClose: false
            }
          ).catch(() => {
            // 用户点击取消或确认，关闭弹窗
          })
          return { allowed: false, quota }
        }
      }
      
      // 额度未超限，清除之前的标记（如果存在）
      clearQuotaExceeded(quotaType)
      return { allowed: true, quota, exceeded: false }
    }
  } catch (error) {
    console.error(`检查${QUOTA_NAME_MAP[quotaType] || quotaType}额度失败:`, error)
    // 额度检查失败不影响操作，默认允许继续（降级处理）
    ElMessage.warning(`额度检查失败，将尝试继续${operationName}`)
    return { allowed: true, quota: null, error: true }
  }
  
  // 默认允许操作
  return { allowed: true, quota: null }
}

/**
 * 检查附件额度（用于附件选择时）
 * @param {number} pendingCount 当前待上传的附件数量
 * @param {number} newFilesCount 新选择的文件数量
 * @param {Object} options 选项
 * @returns {Promise<{allowed: boolean, quota: Object|null}>} 返回是否允许操作和额度信息
 */
export async function checkAttachmentQuota(pendingCount = 0, newFilesCount = 0, options = {}) {
  const { allowLocalUpload = false } = options
  
  // 如果之前已经标记为超限，先获取当前额度信息再判断
  if (isQuotaExceeded(QUOTA_TYPE.ATTACHMENT)) {
    try {
      const quotaResult = await checkQuota(QUOTA_TYPE.ATTACHMENT)
      if (quotaResult && quotaResult.code === '000000' && quotaResult.data) {
        const quotaItem = quotaResult.data
        // 如果 limit 为 null（无限制）或 exceeded 为 false（未超限），清除标记并允许操作
        if (quotaItem.limit === null || quotaItem.exceeded === false) {
          clearQuotaExceeded(QUOTA_TYPE.ATTACHMENT)
          // 继续执行后续的额度检查逻辑
        } else {
          // 确实超限了，弹窗提示
          let message = '您的附件数量已达上限'
          if (quotaItem.limit !== null && quotaItem.used !== null) {
            message += `（${quotaItem.used}/${quotaItem.limit}）`
          }
          message += '，无法继续添加附件。\n\n请到小程序： 我的 -> 会员中心去升级会员或购买附件包以提升额度~'
          await ElMessageBox.alert(
            message,
            '额度已用完',
            {
              confirmButtonText: '确认',
              type: 'warning'
            }
          )
          return { allowed: false, quota: quotaItem }
        }
      } else {
        // 如果获取额度失败，清除标记，继续执行后续逻辑（降级处理）
        clearQuotaExceeded(QUOTA_TYPE.ATTACHMENT)
      }
    } catch (error) {
      // 如果获取额度失败，清除标记，继续执行后续逻辑（降级处理）
      clearQuotaExceeded(QUOTA_TYPE.ATTACHMENT)
      console.warn('检查附件额度失败，清除超限标记:', error)
    }
  }

  try {
    const quotaResult = await checkQuota(QUOTA_TYPE.ATTACHMENT)
    if (quotaResult && quotaResult.data) {
      const quotaItem = quotaResult.data
      
      // 检查是否已经超限
      if (quotaItem.limit !== null && quotaItem.used !== null && quotaItem.exceeded) {
        // 附件额度已超限
        if (allowLocalUpload) {
          // 允许本次上传，但记录状态
          setQuotaExceeded(QUOTA_TYPE.ATTACHMENT)
          await ElMessageBox.confirm(
            `您的附件数量已达上限（${quotaItem.used}/${quotaItem.limit}），本次上传后将无法继续上传。请前往小程序： 我的 -> 会员中心 去升级会员/购买附件包以提升额度。`,
            '附件额度已用完',
            {
              confirmButtonText: '继续上传',
              cancelButtonText: '取消',
              type: 'warning',
              distinguishCancelAndClose: false
            }
          ).catch(() => {
            // 用户点击取消，清除标记
            clearQuotaExceeded(QUOTA_TYPE.ATTACHMENT)
            return { allowed: false, quota: quotaItem }
          })
          return { allowed: true, quota: quotaItem, exceeded: true }
        } else {
          // 不允许上传
          await ElMessageBox.confirm(
            `您的附件数量已达上限（${quotaItem.used}/${quotaItem.limit}），无法继续添加附件。\n\n请到小程序： 我的 -> 会员中心去升级会员或购买附件包以提升额度~`,
            '额度已用完',
            {
              confirmButtonText: '确认',
              cancelButtonText: '取消',
              type: 'warning',
              distinguishCancelAndClose: false
            }
          ).catch(() => {
            // 用户点击取消或确认，关闭弹窗
          })
          return { allowed: false, quota: quotaItem }
        }
      }
      
      // 计算添加后的总数量
      const totalAfterAdd = (quotaItem.used || 0) + pendingCount + newFilesCount
      
      // 如果有限制且添加后会超过限制
      if (quotaItem.limit !== null && quotaItem.limit !== undefined && totalAfterAdd > quotaItem.limit) {
        const remaining = Math.max(0, quotaItem.limit - (quotaItem.used || 0) - pendingCount)
        let message = `您的附件额度已用完，无法继续添加附件。\n\n`
        message += `额度使用情况：\n`
        message += `• 已使用：${quotaItem.used || 0}/${quotaItem.limit} 个\n`
        if (pendingCount > 0) {
          message += `• 待上传：${pendingCount} 个\n`
        }
        message += `• 本次选择：${newFilesCount} 个\n\n`
        if (remaining > 0) {
          message += `您还可以上传 ${remaining} 个附件，请减少本次选择的附件数量。\n\n`
        } else {
          message += `请到小程序： 我的 -> 会员中心去升级会员或购买附件包以提升额度~`
        }
        
        await ElMessageBox.confirm(message, '附件额度不足', {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          type: 'warning',
          distinguishCancelAndClose: false
        }).catch(() => {
          // 用户点击取消或确认，关闭弹窗
        })
        return { allowed: false, quota: quotaItem }
      }
      
      // 额度未超限，清除之前的标记（如果存在）
      clearQuotaExceeded(QUOTA_TYPE.ATTACHMENT)
      return { allowed: true, quota: quotaItem, exceeded: false }
    }
  } catch (error) {
    // 额度检查失败时，记录日志但不阻止用户操作（避免因网络问题影响用户体验）
    console.warn('检查附件额度失败:', error)
    return { allowed: true, quota: null, error: true }
  }
  
  return { allowed: true, quota: null }
}

/**
 * 清除所有额度超限标记（用于用户升级会员后）
 */
export function clearAllQuotaExceeded() {
  Object.values(QUOTA_TYPE).forEach(type => {
    clearQuotaExceeded(type)
  })
}

